/**
 * The HUD combiner: where the symbology is allowed to be.
 *
 * WHY THIS EXISTS
 * A head-up display is a small piece of glass. The symbology on it is
 * collimated — focused at infinity, so it overlays the outside world without
 * your eyes having to refocus — and the price of that is a field of view set by
 * the optics, not by the size of your monitor. On an F-14 it is about twenty
 * degrees. Everything the HUD wants to tell you has to fit inside that, which
 * is why real HUD symbology is so spare, and why pilots talk about losing the
 * ball off the top of the HUD.
 *
 * Drawn edge to edge across the screen instead, the same symbology is a game
 * overlay: the airspeed sits out by your left knee, the pitch ladder spans
 * ninety degrees of sky, and the one skill the display is supposed to build —
 * flying the aeroplane by what is inside that small bright rectangle — is not
 * being practised at all.
 *
 * TWO PROPERTIES FALL OUT OF DOING IT PROPERLY
 *
 * It moves when you look around. The combiner is fixed to the aeroplane, so
 * turning your head slides the symbology across the windscreen and eventually
 * off it. Look over your shoulder at the boat in the break and the HUD is
 * simply not there, which is exactly the situation a real pilot is in.
 *
 * It scales when you zoom. Angular size is angular size: narrow the field of
 * view and the combiner covers more of the screen, because you are magnifying
 * the world it is drawn over along with everything else.
 *
 * No three.js in here — the projection needs a camera, but the sizing is
 * trigonometry and it is worth being able to test it without a GPU.
 */

/**
 * Field of view of the combiner, degrees.
 *
 * This is the INSTANTANEOUS field — the angle the glass subtends from where the
 * pilot's eyes actually are — and not the total optical field, which on the
 * F-14's AN/AVG-8 VDIG is around 20 degrees. The two are different numbers for
 * a real reason: the total field is what the optics can project, and you get to
 * the edges of it by moving your head about the eye box. What you see sitting
 * still is bounded by the frame of the glass, and that is the one that decides
 * how much of the screen the symbology may occupy.
 *
 * 17.8 degrees, because that is what the combiner in this particular model
 * subtends from this particular design eye — measured off the aeroplane rather
 * than picked to look right. Change the aircraft model and this is the number
 * to re-measure, along with HUD_ASPECT and HUD_BORESIGHT_BIAS below.
 */
export const HUD_FOV_DEG = 17.8

/**
 * How much wider the glass is than it is tall.
 *
 * A combiner is a rectangular plate, not a porthole. The circular version of
 * this was wrong twice over: it left the widest part of the glass — the top and
 * bottom corners, where a HUD puts its heading scale and its airspeed — unused,
 * and it trimmed the symbology on a curve no real frame has.
 *
 * 1.14, measured off the plate in the model. It also lands the horizontal field
 * at 20.3 degrees, which is within a rounding error of the AN/AVG-8's quoted
 * 20 — two numbers agreeing from opposite directions is a decent sign the
 * geometry is right rather than merely tuned.
 */
export const HUD_ASPECT = 1.14

/**
 * Depression of the combiner's optical axis below the fuselage reference line,
 * degrees.
 *
 * A HUD is not aimed straight down the aeroplane's nose. The axis is tilted
 * down a little, because everything a pilot uses it for — the aim point, the
 * flight path, the runway — is below the nose rather than above it, and a glass
 * centred on the waterline wastes its top half on empty sky.
 *
 * 0.8 degrees, which is what this model's plate does: its top edge sits just
 * under the canopy bow and its bottom runs down to the glare shield.
 *
 * It is not enough to get everything on the glass, and that is not a bug in the
 * number. On speed the Tomcat carries about 7.8 degrees of pitch on a 3.5
 * degree flight path, so the flight path marker sits ELEVEN degrees below the
 * nose — below the bottom edge of a 17.8 degree combiner however it is aimed.
 * That is a real property of a real aeroplane, and it is why an F-14 approach
 * is flown on the indexer and the ball rather than on the HUD. What the display
 * owes the pilot is to be honest about it: see the caged marker in HUD.js.
 */
export const HUD_DEPRESSION_DEG = 0.8

/** Corner radius, as a fraction of the glass's half-height. */
export const HUD_CORNER = 0.34

/** @deprecated the old name, kept so nothing silently reads undefined. */
export const HUD_TOTAL_FOV_DEG = HUD_FOV_DEG

/**
 * HALF-HEIGHT of the combiner on screen, pixels.
 *
 * Straight perspective: the screen height subtends the camera's vertical field
 * of view, so an angle maps to pixels through the ratio of half-angle tangents.
 * Using degrees-per-pixel directly is the tempting shortcut and it is wrong
 * everywhere except the exact centre of the screen — which is the one place the
 * HUD is not, as soon as the pilot looks anywhere.
 *
 * @param {number} cameraFovDeg the camera's VERTICAL field of view
 * @param {number} screenH pixels
 * @param {number} hudFovDeg the VERTICAL field, total rather than half
 */
export function hudRadiusPx(cameraFovDeg, screenH, hudFovDeg = HUD_FOV_DEG) {
  const halfCam = Math.tan((cameraFovDeg * Math.PI) / 360)
  const halfHud = Math.tan((hudFovDeg * Math.PI) / 360)
  return (screenH / 2) * (halfHud / halfCam)
}

/**
 * The glass itself: a rounded rectangle, centred on the boresight.
 *
 * @param {number} halfHeightPx from `hudRadiusPx`
 */
export function combinerRect(halfHeightPx) {
  return {
    halfW: halfHeightPx * HUD_ASPECT,
    halfH: halfHeightPx,
    corner: halfHeightPx * HUD_CORNER,
    /** Screen-down offset from the boresight to the centre of the glass. */
    centreDy: (HUD_DEPRESSION_DEG * (2 * halfHeightPx)) / HUD_FOV_DEG,
    /** Pixels per degree on the glass, which several callers want. */
    pxPerDeg: (2 * halfHeightPx) / HUD_FOV_DEG,
  }
}

/**
 * The rectangle the fixed symbology is laid out in, just inside the glass.
 *
 * Inset by a few per cent rather than flush, because symbology that touches the
 * frame reads as clipped even when it is not — and because the corners are
 * rounded, so the extreme top and bottom of the tapes have less width available
 * than the middle does.
 */
export function layoutBox(rect) {
  return { w: rect.halfW * 1.92, h: rect.halfH * 1.74 }
}

/**
 * Is any of the combiner on screen?
 *
 * Used to skip the whole HUD when the pilot is looking over their shoulder,
 * which is both faster and the reason a clipped-away HUD does not leave a
 * stray tape hanging at the edge of the canopy.
 */
export function combinerOnScreen(cx, cy, rect, w, h) {
  return (
    cx + rect.halfW > 0 &&
    cx - rect.halfW < w &&
    cy + rect.halfH > 0 &&
    cy - rect.halfH < h
  )
}

/**
 * Trace a rounded rectangle onto a 2D context.
 *
 * Written out rather than using `CanvasRenderingContext2D.roundRect`, which is
 * recent enough that a browser one release behind would silently throw and take
 * the whole HUD with it.
 */
export function roundedRectPath(g, cx, cy, halfW, halfH, corner) {
  const r = Math.min(corner, halfW, halfH)
  const l = cx - halfW
  const t = cy - halfH
  const right = cx + halfW
  const b = cy + halfH
  g.beginPath()
  g.moveTo(l + r, t)
  g.lineTo(right - r, t)
  g.arcTo(right, t, right, t + r, r)
  g.lineTo(right, b - r)
  g.arcTo(right, b, right - r, b, r)
  g.lineTo(l + r, b)
  g.arcTo(l, b, l, b - r, r)
  g.lineTo(l, t + r)
  g.arcTo(l, t, l + r, t, r)
  g.closePath()
}

/**
 * How much of the combiner is filled by a symbol at a given angle off
 * boresight, as a fraction of the radius. Anything at or past 1 is off the
 * glass and must not be drawn.
 */
export function offBoresightFraction(angleDeg, hudFovDeg = HUD_FOV_DEG) {
  return Math.abs(angleDeg) / (hudFovDeg / 2)
}
